import {Actions} from '@redstack/shared'
import {useQuery} from '@tanstack/react-query'
import {createFileRoute} from '@tanstack/react-router'
import {IntakePage} from '@/features/intake/IntakePage'
import {settingsQueryOptions} from '@/hooks/settings'
import {useRequirePermission} from '@/hooks/useRequirePermission'
import {usersListQueryOptions} from '@/hooks/users'
import {fetchPermitWindow} from '@/lib/api/permits'

function IntakeRouteGuard() {
	if (!useRequirePermission(Actions.REVIEW_VIEW)) return null
	return <IntakeRoute />
}

function IntakeRoute() {
	const permitsQuery = useQuery({
		queryKey: [
			'permits',
			{status: 'INBOX', assignedTo: 'unassigned', pagination: false},
		],
		// Matches IntakePage's query exactly — same key, same shape, so the
		// route prefetch warms the cache the page reads.
		queryFn: ({signal}) =>
			fetchPermitWindow(
				{status: ['INBOX'], assignedTo: ['unassigned']},
				{signal, withDescription: true},
			),
		staleTime: 5 * 60_000,
	})

	const settingsQuery = useQuery(settingsQueryOptions())
	const usersQuery = useQuery(usersListQueryOptions())

	const isLoading =
		permitsQuery.isLoading || settingsQuery.isLoading || usersQuery.isLoading

	const error = permitsQuery.error ?? settingsQuery.error ?? usersQuery.error

	if (isLoading) {
		return (
			<div className='px-8 py-6 space-y-4'>
				<div className='h-8 w-48 animate-pulse rounded bg-muted' />
				<div className='h-5 w-64 animate-pulse rounded bg-muted' />
				<div className='space-y-3 mt-6'>
					<div className='h-40 animate-pulse rounded-lg bg-muted' />
					<div className='h-40 animate-pulse rounded-lg bg-muted' />
					<div className='h-40 animate-pulse rounded-lg bg-muted' />
					<div className='h-40 animate-pulse rounded-lg bg-muted' />
				</div>
			</div>
		)
	}

	if (error) {
		return (
			<div className='px-8 py-12'>
				<p className='text-destructive'>
					Failed to load intake data: {error.message}
				</p>
			</div>
		)
	}

	return <IntakePage />
}

export const Route = createFileRoute('/intake/')({
	component: IntakeRouteGuard,
})
